import type { AxiosError } from 'axios'
import { makeAutoObservable } from 'mobx'
import { nanoid } from 'nanoid'
import { type ITooltipProps, showToast } from 'shared/ui'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import type { IRadioBoxProps } from 'shared/ui/Radio'
import { RequestLoadData } from 'shared/api/lib'
import { PhoneApi } from 'entities/Phone'
import { type IParamsReleaseNumber } from 'entities/Phone/api/types'
import { type Phone } from 'entities/Phone/model/Phone'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ReleaseNumberModal } from '../ui/ReleaseNumberModal'

type IReleaseNumberTypes = 'keep' | 'remove'
const releaseNumberOptions: IRadioBoxProps<IReleaseNumberTypes>[] = [
  {
    id: 'keep',
    value: 'keep',
    text: 'Keep number license',
  },
  {
    id: 'remove',
    value: 'remove',
    text: 'Remove number license',
  },
]

export class ReleaseNumberStore {
  private _modalId = nanoid()
  private _isAssigned = false
  private _checkedReleaseNumberOptionId: IReleaseNumberTypes = 'keep'
  private _removeApproveCheckbox = false
  private _checkedNumber: Null<Phone> = null

  constructor(
    private _currentNumber: Phone,
    private _onSuccess: () => void | Promise<void>,
    private _entities: IEntitiesStore
  ) {
    this._isAssigned = _currentNumber.isAssigned
    makeAutoObservable(this)
    this.openReleaseNumberModalModal()
  }

  onSetReleaseNumberOptionId = (optionId: IReleaseNumberTypes) => {
    if (this.isNumbersGracePeriod && optionId === 'remove') return

    this._checkedReleaseNumberOptionId = optionId
  }

  onSetApproveCheckbox = (value: boolean) => {
    this._removeApproveCheckbox = value
  }

  onNumberCheck = (id: number) => {
    this._checkedNumber = this._entities.numbersStore?.getItem(id) || null
  }

  onReleaseNumberModalClose = () => {
    modalStore.removeModal(this._modalId)
  }

  private _requestReleaseNumberModalSubmit = new RequestLoadData({
    requestCallback: async () => {
      if (!this._currentNumber) return

      const params: IParamsReleaseNumber = {
        remove_slot: this._checkedReleaseNumberOptionId === 'remove' ? 1 : 0,
      }
      if (this._checkedNumber) {
        params.reassign_number_id = String(this._checkedNumber.id)
      }
      await PhoneApi.releaseNumber(this._currentNumber.id, params)

      Promise.all([
        this._entities.subscriptionStore.init(),
        this._entities.complianceStore.loadAndUpdateUnverifiedTollFreeNumbers(),
        this._onSuccess(),
      ]).catch(console.error)
      this.onReleaseNumberModalClose()
      showToast({
        type: 'success',
        title: 'Number is released.',
      })
    },
    errorCallback: (e) => {
      const response = (e as AxiosError).response
      const responseData = response?.data as
        | Record<'number' | 'reassign_number_id', string>
        | undefined
      let errorMessage = 'Number not released. Something went wrong.'
      if (response?.status === 422) {
        let validationErrors = null
        if (responseData?.['number'] !== undefined) {
          validationErrors = responseData?.['number']
        } else if (responseData?.['reassign_number_id'] !== undefined) {
          validationErrors = responseData?.['reassign_number_id']
        }

        if (validationErrors) {
          errorMessage = Array.isArray(validationErrors)
            ? validationErrors.join(', ')
            : validationErrors
        }
      }
      showToast({
        type: 'error',
        title: errorMessage,
      })
    },
  })

  onReleaseNumberModalSubmit = () => {
    return this._requestReleaseNumberModalSubmit.blockingLoadData()
  }

  openReleaseNumberModalModal = () => {
    this._modalId = nanoid()

    modalStore.addModal({
      id: this._modalId,
      width: 480,
      title: 'Release number',
      type: ModalTypeList.DEFAULT,
      ModalContent: ReleaseNumberModal,
      ModalContentProps: {
        releaseNumberStoreStore: this,
      },
      paddingContent: '0 24px 16px 24px',
      onClose: this.onReleaseNumberModalClose,
      showCloseButton: false,
    })
  }

  get isReleaseNumberDisabled() {
    if (this._requestReleaseNumberModalSubmit.loading) {
      return true
    }
    if (!this._isAssigned && this._removeApproveCheckbox) {
      return false
    }

    return !(this._isAssigned && this._removeApproveCheckbox && this.checkedNumber)
  }
  get currentPhoneNumber() {
    return this._currentNumber?.formatted_number || ''
  }

  get isNumbersGracePeriod() {
    return this._entities.organizationStore.isNumbersGracePeriod
  }

  get removeOptions() {
    return releaseNumberOptions.map((option) => {
      if (option.value === 'remove') {
        const disabledTooltip: ITooltipProps = {
          placement: 'top',
          label: (
            <>
              You can’t remove a license during
              <br />
              the grace period. Your account
              <br />
              currently has more numbers than
              <br />
              licenses, so this license must stay
              <br />
              active.
            </>
          ),
        }

        return {
          ...option,
          disabled: this.isNumbersGracePeriod,
          tooltip: this.isNumbersGracePeriod
            ? {
                TooltipProps: disabledTooltip,
              }
            : undefined,
        }
      }
      return option
    })
  }

  get removeApproveCheckbox() {
    return this._removeApproveCheckbox
  }
  get checkedReleaseNumberOptionId() {
    return this._checkedReleaseNumberOptionId
  }

  get numbers() {
    return this._entities.numbersStore.numbers.filter(
      (number) => number.formatted_number !== this._currentNumber.formatted_number
    )
  }
  get checkedNumber() {
    return this._checkedNumber
  }
  get isAssigned() {
    return this._isAssigned
  }
}
